import { useCallback, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { resizePanel, type ResizePanelResponse } from './api';

/**
 * Resizable-panel slice (Sebastian ruling 2026-09-17) — resize-panel
 * mutation hook. Companion to `usePanelMove`; same error discipline.
 *
 * Fires `PATCH /api/book/orders/:orderId/scenes/:sceneId/panel-width`
 * when the customer releases a side-edge handle drag. The server may
 * auto-widen the requested width if the persisted text needs more
 * room; the response's `panel` carries the effective drawn geometry.
 * The client MUST reconcile from that.
 *
 * `already_saved` is treated as success (idempotent retry after a
 * network hiccup on identical width).
 */

export type ResizePanelResult =
  | { readonly ok: true; readonly response: ResizePanelResponse }
  | { readonly ok: false; readonly error: ApiError | null };

export interface UsePanelResizeState {
  readonly saving: boolean;
  readonly error: ApiError | null;
  readonly save: (sceneId: string, width: number) => Promise<ResizePanelResult>;
}

export function usePanelResize(orderId: string): UsePanelResizeState {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const save = useCallback(
    async (sceneId: string, width: number): Promise<ResizePanelResult> => {
      setSaving(true);
      setError(null);
      try {
        const response = await resizePanel(orderId, sceneId, width);
        return { ok: true, response };
      } catch (err) {
        const apiError = err instanceof ApiError ? err : null;
        setError(apiError);
        return { ok: false, error: apiError };
      } finally {
        setSaving(false);
      }
    },
    [orderId],
  );

  return { saving, error, save };
}
